// Supabase client implementation
import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
// Replace these with your actual Supabase project details
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://your-project-url.supabase.co';
const supabaseKey = process.env.REACT_APP_SUPABASE_KEY || 'your-anon-key'; // Public anon key, safe to use in browser

// Create client
const supabase = createClient(supabaseUrl, supabaseKey);

console.log('Supabase client initialized with URL:', supabaseUrl);

// Function to test Supabase connection
const testConnection = async () => {
  try {
    console.log('Testing Supabase connection...');
    
    // Simple query to test connection
    const { data, error } = await supabase
      .from('node_graphs')
      .select('count')
      .limit(1);
    
    if (error) throw error;
    
    console.log('Supabase connection test successful!');
    return { success: true };
  } catch (error) {
    console.error('Supabase connection test failed:', error);
    return { success: false, error: error.message };
  }
};

// Function to retrieve node graph data
const getNodeGraphData = async () => {
  try {
    // Add cache busting timestamp to logs to ensure fresh data
    const timestamp = new Date().toISOString();
    console.log(`Fetching node graph data from Supabase... (refresh: ${timestamp})`);
    
    // Clear any previous cached data
    await supabase.auth.refreshSession();
    
    const { data, error } = await supabase
      .from('node_graphs')
      .select('*')
      .eq('graph_id', 'data')
      .single();
    
    if (error) {
      console.error('Error fetching node graph data:', error);
      return null;
    }
    
    console.log('Node graph data retrieved successfully');
    // Return the nodes and links directly
    return {
      nodes: data.nodes || [],
      links: data.links || []
    };
  } catch (error) {
    console.error('Exception fetching node graph data:', error);
    return null;
  }
};

// Function to save node graph data
const saveNodeGraphData = async (data) => {
  try {
    console.log('Saving node graph data to Supabase...');
    
    // Prepare the data payload
    const payload = {
      nodes: data.nodes,
      links: data.links
    };
    
    // Check if record exists
    const { data: existingData } = await supabase
      .from('node_graphs')
      .select('graph_id')
      .eq('graph_id', 'data')
      .single();
    
    let result;
    
    if (existingData) {
      // Update existing record
      result = await supabase
        .from('node_graphs')
        .update(payload)
        .eq('graph_id', 'data');
    } else {
      // Insert new record
      result = await supabase
        .from('node_graphs')
        .insert([{ graph_id: 'data', ...payload }]);
    }
    
    if (result.error) {
      console.error('Error saving node graph data:', result.error);
      return false;
    }
    
    console.log('Node graph data saved successfully');
    return true;
  } catch (error) {
    console.error('Exception saving node graph data:', error);
    return false;
  }
};

// Subscribe to real-time changes
const subscribeToNodeGraphChanges = (callback) => {
  // Set up a real-time subscription to the node_graphs table
  const subscription = supabase
    .channel('node_graphs_changes')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'node_graphs', filter: 'graph_id=eq.data' },
      (payload) => {
        console.log('Real-time update received:', payload);
        if (callback && typeof callback === 'function') {
          // Transform the data to match our expected format
          const updatedData = {
            nodes: payload.new.nodes || [],
            links: payload.new.links || []
          };
          callback(updatedData);
        }
      }
    )
    .subscribe();
  
  // Return unsubscribe function
  return () => {
    console.log('Unsubscribing from real-time updates');
    subscription.unsubscribe();
  };
};

export {
  supabase,
  getNodeGraphData,
  saveNodeGraphData,
  subscribeToNodeGraphChanges,
  testConnection
};
